import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  TemplateRef,
  ChangeDetectorRef,
} from '@angular/core';
import { DataTableColumn, DataTableSortDirection } from 'src/app/shared/ui/data-table/data-table.component';
import { ManagerService, FileService, LocalService, Errors, ProfileService, formatDate } from '../../../../core';
import { TranslateService } from '@ngx-translate/core';
import { managerApi } from '../../../../config';
import { ActivityFilterValue } from '../activity-filter-form/activity-filter-form.component';

@Component({
  selector: 'app-points-activity',
  templateUrl: './points-activity.component.html',
  styleUrls: ['./points-activity.component.scss']
})
export class PointsActivityComponent implements OnInit, AfterViewInit {
  @ViewChild('dateTemplate') dateTemplate!: TemplateRef<any>;
  @ViewChild('netBookingsTemplate') netBookingsTemplate!: TemplateRef<any>;
  @ViewChild('pointsAmountTemplate') pointsAmountTemplate!: TemplateRef<any>;
  @ViewChild('pointActivityTemplate') pointActivityTemplate!: TemplateRef<any>;

  userProfile: any;
  usersList: any[] = [];
  minDate: Date = new Date(2020, 7, 31);
  maxDate: Date = new Date();
  claimedDeals: any;
  claimedDealsPage = 0;
  claimedDealsSortBy = '';
  claimedDealsSortDir: DataTableSortDirection = '';
  claimedDealsLoading = false;
  claimedDealsColumns: DataTableColumn[] = [];
  errors: Errors = { errors: {} };
  pageSize = 5;

  // Last applied filter values (used for pagination/sort re-fetches)
  private lastFilter: ActivityFilterValue = { selectedUsers: [], startDate: null, endDate: null };
  private filterApplied = false;

  constructor(
    private managerService: ManagerService,
    private fileService: FileService,
    private profileService: ProfileService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userProfile = LocalService.get('userProfile');
    this.getClaimedDeals();
    this.getUsersUnderManager();
  }

  ngAfterViewInit(): void {
    this.claimedDealsColumns = [
      { field: 'date', header: this.translate.instant('accountDetails.dateColumn'), align: 'center', sortable: true, cellTemplate: this.dateTemplate },
      { field: 'description', header: this.translate.instant('accountDetails.descriptionColumn'), align: 'center' },
      { field: 'netBookings', header: this.translate.instant('accountDetails.netBookingsColumn'), align: 'center', isBold: true, cellTemplate: this.netBookingsTemplate },
      { field: 'pointsAmount', header: this.translate.instant('accountDetails.pointsAmountColumn'), align: 'center', isBold: true, cellTemplate: this.pointsAmountTemplate },
      { field: 'pointActivity', header: this.translate.instant('accountDetails.pointActivityColumn'), align: 'center', cellTemplate: this.pointActivityTemplate },
      { field: 'comments', header: this.translate.instant('accountDetails.commentsColumn'), align: 'center' }
    ];
    this.cdr.detectChanges();
  }

  onFilterSearch(filter: ActivityFilterValue): void {
    this.lastFilter = filter;
    this.filterApplied = true;
    this.claimedDealsPage = 0;
    this.claimedDealsSortBy = '';
    this.claimedDealsSortDir = '';
    this.claimedDealsLoading = true;
    this.fetchClaimedDealsData(0);
  }

  onFilterReset(): void {
    this.filterApplied = false;
    this.lastFilter = { selectedUsers: [], startDate: null, endDate: null };
    this.claimedDealsPage = 0;
    this.claimedDealsSortBy = '';
    this.claimedDealsSortDir = '';
    this.getClaimedDeals();
  }

  onFilterExport(filter: ActivityFilterValue): void {
    const downloadName = this.translate.instant('accountDetails.downloadPointsActivity');
    const fileName = downloadName + '_' + this.userProfile.company.id + '.xlsx';
    const url = managerApi.pointAccountClaimedByAdmin + '?companyId=' + this.userProfile.company.id + '&size=100000&excel=true';
    this.fileService.excelDownloadFile(url, fileName);
  }

  onClaimedDealsPageChange(newPage: number): void {
    this.claimedDealsPage = newPage - 1;
    if (this.filterApplied) {
      this.fetchClaimedDealsData(newPage - 1);
    } else {
      this.getClaimedDeals(newPage - 1);
    }
  }

  onClaimedDealsSort(event: { sortBy: string; sortDir: DataTableSortDirection }): void {
    this.claimedDealsSortBy = event.sortBy;
    this.claimedDealsSortDir = event.sortDir;
    this.claimedDealsPage = 0;
    if (this.filterApplied) {
      this.fetchClaimedDealsData(0);
    } else {
      this.getClaimedDeals(0);
    }
  }

  splitData(value: any): string {
    return parseFloat(value).toLocaleString();
  }

  getUsersUnderManager(): void {
    this.profileService.companyAllUsers(this.userProfile.company.id).subscribe(
      (data: any[]) => {
        if (data) {
          this.usersList = data.reduce((users: any[], item: any) => {
            if (users.find((obj: any) => obj.id === item.id) === undefined) {
              users.push({ id: item.id, fullName: item.firstName + ' ' + item.lastName });
            }
            return users;
          }, []).sort((a: any, b: any) => a.fullName.localeCompare(b.fullName));
        }
      },
      (err: any) => {
        this.errors = err;
      }
    );
  }

  private getClaimedDeals(pageNumber = 0): void {
    const params: any = {
      page: pageNumber,
      size: this.pageSize,
      companyId: this.userProfile.company.id,
    };
    if (this.claimedDealsSortBy) {
      params.sortBy = this.claimedDealsSortBy;
      params.sortDir = this.claimedDealsSortDir.toUpperCase();
    }
    this.managerService.pointAccountClaimedByAdmin(params).subscribe(
      (data: any) => { this.claimedDeals = data; },
      (err: any) => { this.errors = err; }
    );
  }

  private fetchClaimedDealsData(pageNumber = 0): void {
    const startDate = formatDate(this.lastFilter.startDate, 'YYYY-MM-DD HH:mm:ss');
    const endDate = formatDate(this.lastFilter.endDate, 'YYYY-MM-DD HH:mm:ss');

    const params: any = {
      companyId: this.userProfile.company.id,
      startDate: startDate,
      endDate: endDate,
      page: pageNumber,
      size: this.pageSize,
    };

    if (this.lastFilter.selectedUsers && this.lastFilter.selectedUsers.length > 0) {
      params.userIds = this.lastFilter.selectedUsers.map((u: any) => u.id).join(',');
    }

    if (this.claimedDealsSortBy) {
      params.sortBy = this.claimedDealsSortBy;
      params.sortDir = this.claimedDealsSortDir.toUpperCase();
    }

    this.claimedDealsLoading = true;
    this.managerService.pointAccountClaimedByAdmin(params).subscribe(
      (data: any) => {
        this.claimedDeals = data;
        this.claimedDealsLoading = false;
      },
      (err: any) => {
        this.errors = err;
        this.claimedDealsLoading = false;
      }
    );
  }
}
